import memoize from 'lodash/memoize';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { STATSIG_ROVO_SEARCH_QF_EXPERIENCE_LAYER } from '@atlassian/search-experiment';

import {
	getJiraUIExperienceLayerFrontendExperiment,
	getJiraUIExperienceTenantsLayerFrontendExperiment,
	hasSAINHelloLayerParamValue,
	hasSAINLayerParamValue,
	SAIN_HELLO_LAYER_NAME,
	SAIN_LAYER_NAME,
} from './get-sain-layer-frontend-experiment';

const canRenderLuminaUnmemoized = (): boolean => {
	if (fg('kd_dynamic_layout_in_sain')) {
		return true;
	}

	return false;
};

const isBorderlessUXEnabledUnmemoized = (): boolean => {
	if (hasSAINHelloLayerParamValue('LuminaInSAIN', 'test')) {
		return true;
	}

	if (hasSAINLayerParamValue('LuminaInSAIN', 'test')) {
		return true;
	}

	return false;
};

const isSAINUXUpliftEnabledUnmemoized = (): boolean => {
	const helloLayer = FeatureGates.getLayer(SAIN_HELLO_LAYER_NAME);
	const helloCustomersEnabled = helloLayer.get<boolean>('isSainUXUpliftEnabled', false);

	const customerLayer = FeatureGates.getLayer(SAIN_LAYER_NAME);
	const customersEnabled = customerLayer.get<boolean>('isSainUXUpliftEnabled', false);

	if (helloCustomersEnabled || customersEnabled) {
		return true;
	}

	return false;
};

const isSourcesInShortSAINAnswersEnabledUnmemoized = () => {
	if (fg('kd_sain_show_sources_in_short_answers')) {
		return true;
	}

	return hasSAINLayerParamValue('sourcesInShortSAINAnswers', 'test');
};

const isZeroQuerySuggestionsV1EnabledUnmemoized = (isHello: boolean) => {
	if (isHello) {
		return true;
	}

	const layer = FeatureGates.getLayer(STATSIG_ROVO_SEARCH_QF_EXPERIENCE_LAYER);
	const customersEnabled = layer.get<boolean>('isZeroQuerySuggestionsV1Enabled', false);
	return customersEnabled;
};

const isSourcesSidePanelEnabledUnmemoized = (): boolean => {
	if (hasSAINHelloLayerParamValue('SAINSourcesUplift', 'SourcesInSidePanel')) {
		return true;
	}
	if (hasSAINLayerParamValue('SAINSourcesUplift', 'SourcesInSidePanel')) {
		return true;
	}

	return false;
};

const isSourcesFullWidthEnabledUnmemoized = (): boolean => {
	if (hasSAINHelloLayerParamValue('SAINSourcesUplift', 'FullWidthSAIN')) {
		return true;
	}
	if (hasSAINLayerParamValue('SAINSourcesUplift', 'FullWidthSAIN')) {
		return true;
	}

	return false;
};

const isFullHeightLuminaEnabledUnmemoized = (): boolean => {
	if (fg('kd_enable_full_height_lumina')) {
		return true;
	}
	return false;
};

const isJiraSAINCardSmartLinkEnabledUnmemoized = ({
	sourceProduct,
}: {
	sourceProduct: string | undefined;
}): boolean => {
	const { serveExperiment: serveJiraFPSNLQNavigationExperiment } =
		getJiraUIExperienceLayerFrontendExperiment({
			abTestId: 'rovo_jira_nin_fps_nlq_navigation_exp',
		});

	const { serveExperiment: serveJiraFPSNLQNavigationTenantsExperiment } =
		getJiraUIExperienceTenantsLayerFrontendExperiment({
			abTestId: 'rovo_jira_nin_fps_nlq_navigation_tenants_exp',
		});

	return (
		sourceProduct === 'jira' &&
		(fg('kd_jira_sain_card_smartlink_fg') ||
			serveJiraFPSNLQNavigationExperiment ||
			serveJiraFPSNLQNavigationTenantsExperiment)
	);
};

export const SAINFeatureControl = {
	/** Controls whether frontend can parse and render lumina-formatted answers */
	canRenderLumina: memoize(canRenderLuminaUnmemoized),
	/** Controls the borderless UX experiment (visual design refresh) */
	isBorderlessUXEnabled: memoize(isBorderlessUXEnabledUnmemoized),
	isSAINUXUpliftEnabled: memoize(isSAINUXUpliftEnabledUnmemoized),
	isSourcesSidePanelEnabled: memoize(isSourcesSidePanelEnabledUnmemoized),
	isSourcesFullWidthEnabled: memoize(isSourcesFullWidthEnabledUnmemoized),
	isFullHeightLuminaEnabled: memoize(isFullHeightLuminaEnabledUnmemoized),
	isSourcesInShortSAINAnswersEnabled: memoize(isSourcesInShortSAINAnswersEnabledUnmemoized),
	isZeroQuerySuggestionsV1Enabled: memoize(isZeroQuerySuggestionsV1EnabledUnmemoized),
	isJiraSAINCardSmartLinkEnabled: memoize(isJiraSAINCardSmartLinkEnabledUnmemoized),
};
